/* eslint-disable camelcase */
import { node } from "~/util/dom";
import MyMapsFilter from "~/ui/mymaps/filter";
import { isPremiumNoteUser } from "~/util/user-note";
import ActivitiesSummary from "./summary";
import { IMapyBoxActivityFilter, TMyMapsSortOptions } from "~/interfaces";
import { buildSortSelectBox } from "~/ui/mymaps/sort";

interface IOpts {
	onSortChange: (sortType: TMyMapsSortOptions) => void;
	onFilterChange: (type: "filter" | "reset") => void;
}

function formatDate(date: Date) {
	if (date) {
		return [date.getDate(), date.getMonth() + 1, date.getFullYear()].join(". ");
	}

	return "";
}

export default class Filter {
	_opts: IOpts;

	_dom: {
		container: HTMLDivElement;
		sortArea: HTMLDivElement;
		filterArea: HTMLDivElement;
		infoValue: HTMLSpanElement;
		show: HTMLSpanElement;
		seasonsList: HTMLDivElement;
	};

	_filterCount: number;

	_loadItems: number;

	_empty: boolean;

	_perPage: number;

	_totalCount: number;

	_infoDate: string;

	_season: string;

	_chartsData: Array<{
		items: Array<any>;
		text: any;
		id: any;
		from?: any;
		to?: any;
	}>;

	_filter: MyMapsFilter;

	_page: number;

	_summary: ActivitiesSummary;

	_sort: ReturnType<typeof buildSortSelectBox>;

	constructor(opts: IOpts) {
		this._opts = {
			onSortChange: () => {},
			onFilterChange: () => {},
			...opts,
		};
		this._dom = {
			container: node("div", { className: "filter" }),
			sortArea: node("div", { className: "sort-area" }),
			filterArea: node("div", { className: "filter-area" }),
			infoValue: node("span", { className: "value", innerHTML: "" }),
			show: node("span", { className: "show" }),
			seasonsList: node("div", { className: "list" }),
		};

		this._season = null;
		this._chartsData = null;
		this._empty = false;
		this._filterCount = 0;
		this._perPage = 20;
		this._loadItems = 0;
		this._totalCount = 0;
		this._infoDate = "";
		this._filter = null;
		this._page = 0;
		this._sort = buildSortSelectBox({
			items: "without-custom",
			sortType: "time_desc",
			onChange: value => {
				this._opts.onSortChange(value);
			},
		});
		this._summary = new ActivitiesSummary();

		this._create();
		this.clear();
	}

	get container() {
		return this._dom.container;
	}

	get activitiesFilter(): IMapyBoxActivityFilter | null {
		return this._filter?.changed
			? this._filter.activitiesFilter
			: null;
	}

	set page(page: number) {
		this._page = page;
	}

	get changed() {
		return this._filter?.changed || false;
	}

	nextPage() {
		this._page++;
	}

	async getItems() {
		if (this._filterCount > 0 && this._page > 0 && this._filterCount - (this._perPage * this._page) < 0) {
			return Promise.reject({});
		}

		const curFilter = this._filter?.activitiesFilter || null;
		const filter: any = {
			per_page: this._loadItems ? this._loadItems : this._perPage,
			page: this._page * this._perPage,
			...curFilter ? curFilter : {},
		};

		const data = await Mapy.ProviderMapyBox.trackList(filter);

		if (this._page === 0 && !this._filter?.changed) {
			this._clearFilter();
		}

		if (data.activities.items.length > 0 && !this._filter?.changed && this._page === 0) {
			this._filter = new MyMapsFilter({
				filterData: data.activities.summary,
				premiumType: "premium-visited",
				isPremiumUser: isPremiumNoteUser(),
				onReset: () => {
					this._opts.onFilterChange("reset");
					Mapy.getComponent("logger").log("my_maps_reset_filter", {
						origin: "aktivity",
					});
				},
				onChange: () => {
					this._opts.onFilterChange("filter");
				},
				onAnalytics: type => {
					Mapy.getComponent("logger").log("my_maps_set_filter", {
						origin: "aktivity",
						type,
					});
				},
			});
			this._dom.sortArea.append(this._filter.resetCont);
			this._dom.filterArea.append(this._filter.container);
		}

		this._sort.setValue(data.activities.sort_type);

		const activities = data.activities;
		const items = activities.items || [];
		let minDate = new Date();
		let maxDate = new Date();
		const now = new Date();

		if (curFilter && curFilter.date_from) {
			minDate = new Date(curFilter.date_from);
			maxDate = new Date(curFilter.date_to);
		} else if ("years" in activities.summary) {
			const years = Object.keys(activities.summary.years).sort((firstItem, secondItem) => parseInt(firstItem) < parseInt(secondItem) ? -1 : 1);
			const minY = years[0];
			const maxY = years[years.length - 1];

			if (minY && maxY) {
				minDate = new Date(parseInt(minY), 0, 1, 0, 0, 0);
				//maxDate = new Date(parseInt(maxY), 12, -1, 23, 59, 59);
			}
		}

		this._setInfoDate(minDate, maxDate.getTime() > now.getTime() ? now : maxDate);
		this._summary.summary = activities.summary;
		this._summary.preventAnimation = false;
		this._filterCount = "total_count" in activities.summary && parseInt(activities.summary.total_count.toString()) ? parseInt(activities.summary.total_count.toString()) : 0;

		this._loadItems = 0;

		return items;
	}

	get totalCount() {
		return this._totalCount;
	}

	set preventAnimation(prevent: boolean) {
		this._summary.preventAnimation = prevent;
	}

	get infoDate() {
		return this._infoDate;
	}

	clear() {
		this._infoDate = "";
		this._page = 0;
	}

	_create() {
		this._dom.sortArea.append(this._sort.container);
		this._dom.container.appendChild(this._dom.sortArea);
		this._dom.container.appendChild(this._dom.filterArea);
		this._dom.container.appendChild(this._summary.container);
	}

	_setInfoDate(fromDate: Date, toDate: Date) {
		this._dom.infoValue.innerHTML = `${formatDate(fromDate)} &#0150; ${formatDate(toDate)}`;
		this._infoDate = this._dom.infoValue.textContent;
	}

	_clearFilter() {
		if (this._filter) {
			this._filter.destroy();
			this._filter.container.remove();
			this._filter.resetCont.remove();
			this._filter = null;
		}

		this._dom.filterArea.innerHTML = "";
	}
}
